import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Avatar, Modal, SegmentedControl } from "@perlab/ui";
import { useAuth as useShellAuth } from "@perlab/auth-bridge";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BookOpen, Keyboard, LogOut, Sparkles, Activity, Compass } from "lucide-react";
import { navTo } from "./ContextChain";
import { useContextChain } from "../state/ContextChain";
import { useAuth as useSessionAuth } from "../../lib/auth/AuthContext";
import { useProfile } from "../api/useProfile";
import { useAccountTheme } from "../state/useAccountTheme";
import { HelpDrawer } from "./HelpDrawer";
import { ChangelogViewer } from "./ChangelogViewer";
import { SystemStatusPanel } from "./SystemStatusPanel";
import { OnboardingTour } from "./OnboardingTour";
import { AskAnandWidget } from "./AskAnandWidget";

// Topbar floating menus (radix overlays — never inline). AccountMenu carries the theme and
// identity. HelpMenu keeps the familiar question-mark trigger while opening the integrated Ask
// Anand actions and the real documentation, shortcuts, changelog, status, and onboarding surfaces.
// Row 17.1 — HelpMenu is string-externalized (a CAT17 proof surface); AccountMenu is NOT
// (a disclosed partial pass per that surface, not hidden — see that batch's closeout).
const contentCls =
  "z-50 min-w-[16rem] rounded-[2px] border border-[var(--color-border-default)] bg-[var(--surface-sunken)] p-1 shadow-[var(--shadow-lg)]";
const itemCls =
  "flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--color-text-primary)] rounded-sm cursor-pointer outline-none select-none data-[highlighted]:bg-[var(--color-surface-muted)]";
const sepCls = "my-1 h-px bg-[var(--color-border-subtle)]";
const resourceBtnCls =
  "flex min-h-9 items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs text-[var(--color-text-primary)] "
  + "transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]";

function deriveInitials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length >= 2) return (p[0]!.charAt(0) + p[p.length - 1]!.charAt(0)).toUpperCase();
  return (p[0]?.slice(0, 2) ?? "?").toUpperCase();
}

// Real, wired shortcuts only (⌘K = ShellCommandPalette; ⌘B = SidebarProvider; ⌘\ = RightInspector).
const SHORTCUTS: { keys: string[]; labelKey: string }[] = [
  { keys: ["⌘", "K"], labelKey: "shortcuts.openCommandPalette" },
  { keys: ["⌘", "B"], labelKey: "shortcuts.toggleSidebar" },
  { keys: ["⌘", "\\"], labelKey: "shortcuts.toggleContextInspector" },
];

export function HelpMenu() {
  const { t } = useTranslation();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  return (
    <>
      <AskAnandWidget
        renderResources={(close) => (
          <section aria-labelledby="ask-anand-resources-title">
            <h3 id="ask-anand-resources-title" className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
              More help
            </h3>
            <div className="mt-2 grid grid-cols-2 gap-1">
              <button type="button" className={resourceBtnCls} onClick={() => { close(); setDocsOpen(true); }}>
                <BookOpen size={15} aria-hidden /> {t("help.documentation")}
              </button>
              <button type="button" className={resourceBtnCls} onClick={() => { close(); setShortcutsOpen(true); }}>
                <Keyboard size={15} aria-hidden /> {t("help.keyboardShortcuts")}
              </button>
              <button type="button" className={resourceBtnCls} onClick={() => { close(); setTourOpen(true); }}>
                <Compass size={15} aria-hidden /> {t("help.onboardingTour")}
              </button>
              <button type="button" className={resourceBtnCls} onClick={() => { close(); setChangelogOpen(true); }}>
                <Sparkles size={15} aria-hidden /> {t("help.whatsNew")}
              </button>
              <button type="button" className={resourceBtnCls} onClick={() => { close(); setStatusOpen(true); }}>
                <Activity size={15} aria-hidden /> {t("help.systemStatus")}
              </button>
            </div>
          </section>
        )}
      />

      <Modal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} title={t("shortcuts.title")}>
        <dl className="flex flex-col gap-2">
          {SHORTCUTS.map((s) => (
            <div key={s.labelKey} className="flex items-center justify-between gap-6">
              <dt className="text-sm text-[var(--color-text-primary)]">{t(s.labelKey)}</dt>
              <dd className="flex gap-1">
                {s.keys.map((k) => (
                  <kbd
                    key={k}
                    className="inline-flex h-6 min-w-6 items-center justify-center rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-muted)] px-1.5 text-xs font-medium text-[var(--color-text-secondary)]"
                  >
                    {k}
                  </kbd>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </Modal>

      <HelpDrawer open={docsOpen} onClose={() => setDocsOpen(false)} />
      <ChangelogViewer open={changelogOpen} onClose={() => setChangelogOpen(false)} />
      <SystemStatusPanel open={statusOpen} onClose={() => setStatusOpen(false)} />
      <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </>
  );
}

export function AccountMenu() {
  const { user, logout } = useSessionAuth();
  const { user: shellUser } = useShellAuth();
  const role = shellUser?.roles?.join(", ") || "No role";
  const { workspaceId } = useContextChain();
  const navigate = useNavigate();
  const { theme, setTheme } = useAccountTheme(); // account-canonical: toggle write-throughs to the DB
  const { data: profile } = useProfile();
  const name = user?.name ?? user?.preferred_username ?? user?.email ?? "Account";
  const initials = deriveInitials(name);
  const avatarSrc = profile?.avatar_data_url ?? undefined;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Account: ${name}`}
          title={name}
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
        >
          <Avatar
            src={avatarSrc}
            fallback={initials}
            alt={name}
            size="sm"
            className="ring-1 ring-[var(--color-accent)]/60"
          />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content side="bottom" align="end" sideOffset={8} className={contentCls}>
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">{name}</p>
            {user?.email && <p className="text-xs text-[var(--color-text-secondary)]">{user.email}</p>}
            <p className="text-xs font-medium text-[var(--color-accent)]">{role}</p>
            <p className="text-xs text-[var(--color-text-secondary)] font-mono">{workspaceId}</p>
          </div>
          <DropdownMenu.Separator className={sepCls} />
          <div className="px-3 py-2">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">Theme</p>
            <SegmentedControl
              aria-label="Theme"
              options={[{ value: "light", label: "Light" }, { value: "dark", label: "Dark" }, { value: "system", label: "System" }]}
              value={theme}
              onChange={(v) => setTheme(v as "light" | "dark" | "system")}
            />
          </div>
          <DropdownMenu.Separator className={sepCls} />
          <DropdownMenu.Item className={itemCls} onSelect={() => navigate(navTo.profile(workspaceId))}>Profile</DropdownMenu.Item>
          <DropdownMenu.Item className={itemCls} onSelect={() => navigate(navTo.settings(workspaceId))}>Preferences</DropdownMenu.Item>
          <DropdownMenu.Item className={itemCls} onSelect={() => navigate(navTo.notifications(workspaceId))}>Notifications</DropdownMenu.Item>
          <DropdownMenu.Separator className={sepCls} />
          <DropdownMenu.Item
            className={`${itemCls} text-[var(--color-accent)] data-[highlighted]:text-[var(--color-accent)]`}
            onSelect={() => { void logout().then(() => navigate({ to: "/login" })); }}
          >
            <LogOut size={15} aria-hidden /> Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
